import { authedApiFetch } from 'utils/Api';

const buildQueryParams = ({ page, perPage, filters, keyMap = {} }) => {
	const params = new URLSearchParams();
	// The stage summary is unpaginated, so page/per_page are omitted for it
	if (page != null) params.set('page', page);
	if (perPage != null) params.set('per_page', perPage);

	Object.entries(filters).forEach(([key, value]) => {
		if (value !== '' && value != null) {
			const filterKey = keyMap[key] || key;
			params.set(`filters[${filterKey}]`, value);
		}
	});

	return params;
};

const salesforceEntitiesService = {
	// filters: { search, entityType, status }. Blank values are omitted so the API
	// treats them as "All Types"/"All Statuses".
	getEntities: async (pageNumber = 1, perPage = 25, filters = {}) => {
		const params = buildQueryParams({
			page: pageNumber,
			perPage,
			filters,
			keyMap: { entityType: 'entity_type' },
		});

		const response = await authedApiFetch({
			endpoint: `/salesforce_entities?${params.toString()}`,
			method: 'GET',
		});
		return response;
	},
	getPicklistValues: async () => {
		return await authedApiFetch({
			endpoint: '/salesforce_entities/picklist_values',
			method: 'GET',
		});
	},
	searchEntities: async (search, perPage = 25) => {
		const params = buildQueryParams({
			page: 1,
			perPage,
			filters: { search },
		});

		const response = await authedApiFetch({
			endpoint: `/salesforce_entities?${params.toString()}`,
			method: 'GET',
		});
		return response;
	},
	getEntity: async (entityId) => {
		const response = await authedApiFetch({
			endpoint: `/salesforce_entities/${entityId}`,
			method: 'GET',
		});
		return response;
	},
  
	updateEntity: async (entityId, fields) => {
		return await authedApiFetch({
			endpoint: `/salesforce_entities/${entityId}`,
			method: 'PATCH',
			payload: { entity: fields },
		});
	},
  
	getFundraisings: async (entityId, page = 1, perPage = 25, filters = {}, sort = {}) => {
		const params = buildQueryParams({ page, perPage, filters });

		if (sort?.sortBy) {
			params.set('sort_by', sort.sortBy);
			params.set('sort_dir', sort.sortDir === 'desc' ? 'desc' : 'asc');
		}

		const response = await authedApiFetch({
			endpoint: `/salesforce_entities/${entityId}/salesforce_fundraisings?${params.toString()}`,
			method: 'GET',
		});
		return {
			data: response?.fundraisings || [],
			totalCount: response?.total || 0,
		};
	},
	// Distinct Internal Coverage names across all fundraising opportunities for the entity.
	// No filters applied so the list never shrinks while a coverage filter is active.
	getInternalCoverageOptions: async (entityId) => {
		const response = await authedApiFetch({
			endpoint: `/salesforce_entities/${entityId}/salesforce_fundraisings/internal_coverage_options`,
			method: 'GET',
		});
		return response?.names || [];
	},
	// Commitment totals grouped by stage for the same filters the pipeline table uses,
	// so the chart reflects exactly what the table is showing.
	getFundraisingStageSummary: async (entityId, filters = {}) => {
		const params = buildQueryParams({ filters });
		const query = params.toString();

		const response = await authedApiFetch({
			endpoint: `/salesforce_entities/${entityId}/salesforce_fundraisings/stage_summary${
				query ? `?${query}` : ''
			}`,
			method: 'GET',
		});
		return {
			stageSummary: response?.stage_summary || [],
			totalCommitment: response?.total_commitment || 0,
		};
	},
};

export default salesforceEntitiesService;
